import React, { useCallback, useEffect, useRef } from 'react';
import { SatCoreRegister } from '../SatCoreRegistry';

/**
 * MultiControlledCarouselsObserver
 *
 * A React utility component that manages tabbing behavior across carousel instances
 * with improved performance and reduced DOM operations.
 * Uses an event-driven approach for detecting ControlledCarouselContainer changes.
 *
 * @example
 * <ExampleParentComponent>
 *     <MultiControlledCarouselsObserver />
 *     <ControlledCarouselContainer carouselId='example-carousel-1' />
 *     <ControlledCarouselContainer carouselId='example-carousel-2' />
 *     <ControlledCarouselContainer carouselId='example-carousel-3' />
 * </ExampleParentComponent>
 */
const MultiControlledCarouselsObserver = () => {
  // Ref for the observer element
  const observerRef = useRef(null);

  // Store reference to mutation observer for cleanup
  const mutationObserverRef = useRef(null);
  // Store reference to carousel DOM elements for efficient access
  const carouselElementsRef = useRef([]);

  // Debounce control
  const debounceTimerRef = useRef(null);
  // Constants for timing
  const DEBOUNCE_DELAY = 150;
  const INITIAL_SETUP_DELAY = 300;
  const FALLBACK_RESCAN_INTERVAL = 10000; // Increased to 10 seconds as a fallback mechanism

  // Helper to debounce frequent operations
  const debounce = useCallback((callback) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      callback();
      debounceTimerRef.current = null;
    }, DEBOUNCE_DELAY);
  }, []);

  // Cache carousel containers to avoid repeatedly querying the DOM
  const updateCarouselElements = useCallback(() => {
    carouselElementsRef.current = Array.from(
      document.querySelectorAll('.controlled-carousel-container')
    );
  }, []);

  // Update tab indexes efficiently by targeting only the relevant elements
  const updateTabIndexes = useCallback(() => {
    // Get all carousel containers if needed
    if (carouselElementsRef.current.length === 0) {
      updateCarouselElements();
    }

    // Process each carousel container
    carouselElementsRef.current.forEach((container) => {
      // Process arrows in this container
      const navArrows = container.querySelectorAll('.slick-arrow');
      navArrows.forEach((arrow) => {
        const isVisible = window.getComputedStyle(arrow).display !== 'none';
        if (arrow.getAttribute('tabindex') !== (isVisible ? '0' : '-1')) {
          arrow.setAttribute('tabindex', isVisible ? '0' : '-1');
        }
      });
      // Process active slide buttons in this container
      const slides = container.querySelectorAll('.slick-slide');
      slides.forEach((slide) => {
        const isActive = slide.classList.contains('slick-active');
        const buttons = slide.querySelectorAll('button');

        buttons.forEach((button) => {
          if (button.getAttribute('tabindex') !== (isActive ? '0' : '-1')) {
            button.setAttribute('tabindex', isActive ? '0' : '-1');
          }
          if (button.getAttribute('aria-hidden') !== (isActive ? 'false' : 'true')) {
            button.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          }
        });
      });
    });
  }, [updateCarouselElements]);

  // Efficiently handle events that should trigger tab index updates
  const updateTabbableElements = useCallback(() => {
    // Immediate update
    updateTabIndexes();

    // Delayed update to catch animations/transitions
    debounce(updateTabIndexes);
  }, [updateTabIndexes, debounce]);

  // Setup mutation observer to watch for changes to carousel elements
  const setupMutationObserver = useCallback(() => {
    // Disconnect any existing observer
    if (mutationObserverRef.current) {
      mutationObserverRef.current.disconnect();
    }

    // Create new observer
    mutationObserverRef.current = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      // Check mutations for relevant changes
      for (const mutation of mutations) {
        // Only process carousel-related mutations
        const targetNode = mutation.target;
        const isCarouselElement =
          targetNode.closest('.controlled-carousel-container') ||
          targetNode.classList?.contains('controlled-carousel-container');
        if (isCarouselElement) {
          if (
            (mutation.type === 'attributes' &&
              (mutation.attributeName === 'class' || mutation.attributeName === 'style')) ||
            mutation.type === 'childList'
          ) {
            shouldUpdate = true;
            break;
          }
        }
      }
      if (shouldUpdate) {
        debounce(updateTabIndexes);
      }
    });

    // If we have carousel elements, observe them directly
    if (carouselElementsRef.current.length > 0) {
      carouselElementsRef.current.forEach((container) => {
        mutationObserverRef.current.observe(container, {
          attributeFilter: ['class', 'style'],
          attributes: true,
          childList: true,
          subtree: true
        });
      });
    } else {
      // Fallback to observing the body but with optimized filtering
      mutationObserverRef.current.observe(document.body, {
        attributeFilter: ['class', 'style'],
        attributes: true,
        childList: true,
        subtree: true
      });
    }
  }, [debounce, updateTabIndexes]);

  // Set up event listeners and mutation observer
  useEffect(() => {
    // Initial carousel element detection
    updateCarouselElements();

    // Handle click events that might change the active slides
    const handleClick = (e) => {
      const clickedButton = e.target.closest('button');
      if (clickedButton && (
        clickedButton.closest('.controlled-carousel-container .slick-slide') ||
        clickedButton.classList.contains('slick-arrow')
      )) {
        debounce(updateTabIndexes);
      }
    };

    // Handle carousel added event
    const handleCarouselAdded = (e) => {
      updateCarouselElements();
      setupMutationObserver();
      updateTabbableElements();
    };

    // Handle carousel removed event
    const handleCarouselRemoved = (e) => {
      updateCarouselElements();
      setupMutationObserver();
      updateTabbableElements();
    };

    // Set up initial observer
    setupMutationObserver();

    // Timers to clear on cleanup
    const timers = [];

    // Initial tab index setup
    const initialTimer = setTimeout(updateTabbableElements, INITIAL_SETUP_DELAY);
    timers.push(initialTimer);

    // Add event listeners
    document.addEventListener('click', handleClick);
    document.addEventListener('controlled-carousel:added', handleCarouselAdded);
    document.addEventListener('controlled-carousel:removed', handleCarouselRemoved);

    // Handle resize events
    const handleResize = () => debounce(updateTabbableElements);
    window.addEventListener('resize', handleResize);

    // Fallback check for carousels that might have been added without events
    const fallbackRescanCarousels = () => {
      const currentCarousels = document.querySelectorAll('.controlled-carousel-container');
      if (currentCarousels.length !== carouselElementsRef.current.length) {
        updateCarouselElements();
        setupMutationObserver();
        updateTabbableElements();
      }
    };
    const fallbackTimer = setInterval(fallbackRescanCarousels, FALLBACK_RESCAN_INTERVAL);
    timers.push(fallbackTimer);

    // Cleanup function
    return () => {
      // Clear all timers
      timers.forEach((timer) => clearTimeout(timer));
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Remove event listeners
      document.removeEventListener('click', handleClick);
      document.removeEventListener('controlled-carousel:added', handleCarouselAdded);
      document.removeEventListener('controlled-carousel:removed', handleCarouselRemoved);
      window.removeEventListener('resize', handleResize);

      // Disconnect mutationObserverRef.current
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
      }
    };
  }, [
    updateCarouselElements,
    updateTabIndexes,
    updateTabbableElements,
    setupMutationObserver,
    debounce
  ]);

  // Using an empty div with display:none to avoid affecting the DOM structure
  return <div ref={observerRef} aria-hidden='true' style={{ display: 'none' }} />;
};

SatCoreRegister('MultiControlledCarouselsObserver', MultiControlledCarouselsObserver);

export default MultiControlledCarouselsObserver;
